import { useCallback, useMemo, useRef } from 'react';
import {
  Button,
  Combobox,
  ComboboxChip,
  ComboboxChips,
  ComboboxChipsInput,
  ComboboxCollection,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxItem,
  ComboboxList,
  FieldDescription,
} from '@monksflow/monks-ui';
import { Building2Icon, InfoIcon, ShieldIcon, UsersIcon, XIcon } from 'lucide-react';
import { useKnowledgeBasesList } from '../../model/useKnowledgeBasesList';
import type { SchemaWidgetProps } from '@/shared/ui/SchemaForm';
import type { EnhancedKnowledgeBaseSummaryDto } from '@inference.engine/assets-api-client/web';
import { useTranslation } from 'react-i18next';

const MAX_KB_COUNT = 5;

function KbIcon({ isInternal, hasWorkspace }: { isInternal: boolean; hasWorkspace: boolean }) {
  if (isInternal) return <ShieldIcon className="size-3.5 text-muted-foreground" />;
  if (hasWorkspace) return <UsersIcon className="size-3.5 text-muted-foreground" />;
  return <Building2Icon className="size-3.5 text-muted-foreground" />;
}

/**
 * Multi-select KB widget.
 * Limited to maximum 5 knowledge bases per opportunity.
 */
export function KbMultiSelectWidget({
  id,
  label,
  value,
  onChange,
  schema,
  disabled,
  hasError,
}: SchemaWidgetProps) {
  const { t } = useTranslation('knowledge');

  const { data: knowledgeBases = [], isLoading } = useKnowledgeBasesList(true);

  const placeholder = t('knowledgeBases.kbMultiSelectWidget.selectKnowledgeBases');

  // The schema form pads an array up to its `minItems`, so a configuration that has never held a
  // knowledge base arrives as `[null]`. Kept, that empty slot counts as a selection, reports itself
  // as a knowledge base belonging to another workspace, and rides along into the save payload,
  // where the schema rejects the whole thing.
  const selectedKbIds = useMemo(
    () =>
      Array.isArray(value)
        ? value.filter((id): id is string => typeof id === 'string' && id !== '')
        : [],
    [value],
  );

  const sorted = useMemo(() => {
    return [...knowledgeBases].sort((a, b) => {
      // Foundation knowledge bases lead the list: they apply org-wide, so they are
      // the ones a reader scanning for the right scope needs to see first.
      if (a.isInternal !== b.isInternal) return a.isInternal ? -1 : 1;
      return (a.name || a.id).localeCompare(b.name || b.id);
    });
  }, [knowledgeBases]);

  const selectedKbs = useMemo(() => {
    return knowledgeBases.filter((kb) => selectedKbIds.includes(kb.id));
  }, [knowledgeBases, selectedKbIds]);

  // The list is scoped to the workspace being browsed, so a knowledge base owned by the workspace
  // this configuration belongs to is absent when an admin edits it from elsewhere. Those ids are
  // carried through every edit: rendering them as unselected would delete them on the next save,
  // silently and without the admin ever seeing what was lost.
  const hiddenKbIds = useMemo(() => {
    // Mid-load the list is empty, which would make every selection look hidden.
    if (isLoading) return [];
    const known = new Set(knowledgeBases.map((kb) => kb.id));
    return selectedKbIds.filter((kbId) => !known.has(kbId));
  }, [isLoading, knowledgeBases, selectedKbIds]);

  const selectedCount = selectedKbs.length + hiddenKbIds.length;
  const isLimitReached = selectedCount >= MAX_KB_COUNT;
  const isOverLimit = selectedCount > MAX_KB_COUNT;

  // Anchor the popup on the full-width chips row rather than the inner input,
  // which shrinks as chips fill in.
  const anchorRef = useRef<HTMLDivElement>(null);

  const emit = useCallback(
    (ids: string[]) => {
      onChange(ids.length > 0 ? ids : undefined);
    },
    [onChange],
  );

  const handleValueChange = useCallback(
    (next: EnhancedKnowledgeBaseSummaryDto[]) => {
      emit([...hiddenKbIds, ...next.map((kb) => kb.id)]);
    },
    [emit, hiddenKbIds],
  );

  const handleRemove = useCallback(
    (kbId: string) => {
      emit(selectedKbIds.filter((current) => current !== kbId));
    },
    [emit, selectedKbIds],
  );

  const itemToStringLabel = useCallback(
    (kb: EnhancedKnowledgeBaseSummaryDto) => kb.name || kb.id,
    [],
  );
  const itemToStringValue = useCallback((kb: EnhancedKnowledgeBaseSummaryDto) => kb.id, []);
  const isItemEqualToValue = useCallback(
    (a: EnhancedKnowledgeBaseSummaryDto, b: EnhancedKnowledgeBaseSummaryDto) => a.id === b.id,
    [],
  );

  return (
    <div className="flex flex-col gap-1">
      <Combobox<EnhancedKnowledgeBaseSummaryDto, true>
        multiple
        items={sorted}
        value={selectedKbs}
        onValueChange={handleValueChange}
        disabled={disabled}
        itemToStringLabel={itemToStringLabel}
        itemToStringValue={itemToStringValue}
        isItemEqualToValue={isItemEqualToValue}
      >
        <ComboboxChips ref={anchorRef} aria-invalid={hasError || isOverLimit ? true : undefined}>
          {selectedKbs.map((kb) => (
            // The built-in remove control has no name of its own, so the chip
            // supplies one that says which knowledge base it drops.
            <ComboboxChip key={kb.id} showRemove={false}>
              <KbIcon isInternal={kb.isInternal} hasWorkspace={Boolean(kb.workspaceId)} />
              {kb.name || kb.id}
              <Button
                variant="ghost"
                size="icon-mini"
                className="-mr-1 ml-1 size-4 opacity-50 hover:opacity-100"
                aria-label={t('knowledgeBases.kbMultiSelectWidget.removeKnowledgeBase', {
                  name: kb.name || kb.id,
                })}
                disabled={disabled}
                onClick={() => handleRemove(kb.id)}
              >
                <XIcon className="size-3" />
              </Button>
            </ComboboxChip>
          ))}
          <ComboboxChipsInput
            id={id}
            placeholder={selectedCount === 0 ? placeholder : undefined}
            aria-label={label}
            className="pl-0"
          />
        </ComboboxChips>

        <ComboboxContent anchor={anchorRef} className="pointer-events-auto">
          <ComboboxList>
            <ComboboxCollection>
              {(kb: EnhancedKnowledgeBaseSummaryDto) => (
                <ComboboxItem
                  key={kb.id}
                  value={kb}
                  // Past the cap only the already-picked rows stay live, so the
                  // list still lets you swap one out rather than locking entirely.
                  disabled={isLimitReached && !selectedKbIds.includes(kb.id)}
                >
                  <KbIcon isInternal={kb.isInternal} hasWorkspace={Boolean(kb.workspaceId)} />
                  {kb.name || kb.id}
                </ComboboxItem>
              )}
            </ComboboxCollection>
            <ComboboxEmpty>
              {t('knowledgeBases.kbMultiSelectWidget.noKnowledgeBasesFound')}
            </ComboboxEmpty>
          </ComboboxList>
        </ComboboxContent>
      </Combobox>

      <p className={isOverLimit ? 'text-sm text-destructive' : 'text-sm text-muted-foreground'}>
        {t('knowledgeBases.kbMultiSelectWidget.knowledgeBasesSelected', {
          selected: selectedCount,
          max: MAX_KB_COUNT,
        })}
        {isLimitReached && t('knowledgeBases.kbMultiSelectWidget.maximumReached')}
        {isOverLimit && t('knowledgeBases.kbMultiSelectWidget.reduceToSave')}
      </p>
      {hiddenKbIds.length > 0 && (
        // Carries its own weight rather than sitting in the helper-text grey: it reports a
        // selection the field cannot show, which a reader skimming past would misread as absent.
        <p className="flex items-center gap-1 text-sm font-medium text-foreground">
          <InfoIcon className="size-3.5" aria-hidden />
          {t('knowledgeBases.kbMultiSelectWidget.notVisibleHere', { count: hiddenKbIds.length })}
        </p>
      )}
      {schema.description && <FieldDescription>{schema.description}</FieldDescription>}
    </div>
  );
}
